import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Accordion, Alert, Button, Modal, Table } from 'react-bootstrap'
import { useAdminConfigKeySyncRun, useAdminConfigKeySyncRuns } from 'api/hooks/use-admin-analytic-configs'
import AppBadge from 'shared/badges/app-badge'
import CenteredSpinner from 'shared/centered-spinner'

const DRIFT_SECTIONS = [
  'missing_definitions',
  'definition_mismatches',
  'unread_attachments',
  'unknown_analytics',
  'code_problems'
]

const formatTime = (time) => new Date(time).toLocaleString()

const driftCount = (counts) => DRIFT_SECTIONS.reduce((sum, section) => sum + (counts[section] || 0), 0)

// JSON keeps whitespace visible, which is often the whole difference (e.g. "write off ").
const literal = (value) => JSON.stringify(value)

const AnalyticLink = ({ analyticKey, analyticIds }) => {
  const id = analyticIds[analyticKey]
  const label = <code>{analyticKey}</code>
  return id ? <Link to={`/admin/analytics-data/${id}`}>{label}</Link> : label
}

const AnalyticKeyTable = ({ rows, analyticIds }) => (
  <Table size="sm" bordered className="mb-0">
    <thead>
      <tr>
        <th>Analytic</th>
        <th>Config key</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={`${row.analytic_key}-${row.config_key}`}>
          <td>
            <AnalyticLink analyticKey={row.analytic_key} analyticIds={analyticIds} />
          </td>
          <td>
            <code>{row.config_key}</code>
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
)

const CodeList = ({ items }) => (
  <ul className="mb-0">
    {items.map((item) => (
      <li key={item}>
        <code>{item}</code>
      </li>
    ))}
  </ul>
)

const DefaultDifference = ({ app, code }) => {
  if (!Array.isArray(app) || !Array.isArray(code)) {
    return (
      <>
        <code>{literal(app)}</code> → <code>{literal(code)}</code>
      </>
    )
  }
  const appItems = app.map(literal)
  const codeItems = code.map(literal)
  const added = codeItems.filter((item) => !appItems.includes(item))
  const removed = appItems.filter((item) => !codeItems.includes(item))
  if (added.length === 0 && removed.length === 0) return <span className="text-muted">Same items, different order</span>

  return (
    <div data-testid="default-difference">
      {added.map((item) => (
        <div key={`+${item}`} className="text-success">
          + <code>{item}</code> <span className="small text-muted">in the code only</span>
        </div>
      ))}
      {removed.map((item) => (
        <div key={`-${item}`} className="text-danger">
          − <code>{item}</code> <span className="small text-muted">in the app only</span>
        </div>
      ))}
    </div>
  )
}

const MismatchTable = ({ rows }) => (
  <Table size="sm" bordered className="mb-0">
    <thead>
      <tr>
        <th>Config key</th>
        <th>Datatype (app → code)</th>
        <th>Default</th>
      </tr>
    </thead>
    <tbody>
      {rows.map(({ config_key: key, app, code }) => (
        <tr key={key}>
          <td>
            <code>{key}</code>
          </td>
          <td>{'datatype' in code ? `${app.datatype} → ${code.datatype}` : <span className="text-muted">Same</span>}</td>
          <td>
            {'default' in code ? (
              <DefaultDifference app={app.default} code={code.default} />
            ) : (
              <span className="text-muted">Same</span>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
)

const MissingDefinitionsTable = ({ rows, analyticIds }) => (
  <Table size="sm" bordered className="mb-0">
    <thead>
      <tr>
        <th>Config key</th>
        <th>Read by</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.config_key}>
          <td>
            <code>{row.config_key}</code>
          </td>
          <td>
            {row.analytic_keys.map((analyticKey) => (
              <span key={analyticKey} className="me-2">
                <AnalyticLink analyticKey={analyticKey} analyticIds={analyticIds} />
              </span>
            ))}
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
)

const SECTIONS = [
  {
    key: 'created_keys',
    title: 'Keys created',
    help: 'Definitions created from the code’s datatype and default. They have no label or description until an admin adds one.',
    render: (items) => <CodeList items={items} />
  },
  {
    key: 'attached',
    title: 'Keys attached',
    help: 'Keys the code reads that were attached to their analytic, so they now appear on its Configuration tab.',
    render: (rows, analyticIds) => <AnalyticKeyTable rows={rows} analyticIds={analyticIds} />
  },
  {
    key: 'defaults_backfilled',
    title: 'Default values set',
    help: 'Municipality values set to a newly attached key’s default, for municipalities with the analytic’s module and no value yet.',
    render: (count) => <span>{count.toLocaleString()} municipality values set to the key’s default.</span>
  },
  {
    key: 'missing_definitions',
    title: 'Missing definitions',
    help: 'Keys the code reads but can’t give a default for. Define them on an analytic’s Configuration tab and the next sync attaches them.',
    render: (rows, analyticIds) => <MissingDefinitionsTable rows={rows} analyticIds={analyticIds} />
  },
  {
    key: 'definition_mismatches',
    title: 'Definition mismatches',
    help: 'The app’s definition differs from the code. Municipalities are seeded with the app’s default, so one side is likely wrong.',
    render: (rows) => <MismatchTable rows={rows} />
  },
  {
    key: 'unread_attachments',
    title: 'Attached but not read by code',
    help: 'Keys attached to an analytic whose code doesn’t read them, so changing them changes nothing.',
    render: (rows, analyticIds) => <AnalyticKeyTable rows={rows} analyticIds={analyticIds} />
  },
  {
    key: 'unknown_analytics',
    title: 'Analytics missing from the app',
    help: 'Analytics whose code reads config but that have no default analytic in the app, so there is nothing to attach their keys to.',
    render: (items) => <CodeList items={items} />
  },
  {
    key: 'code_problems',
    title: 'Code problems',
    help: 'Config reads in the analytics code the sync couldn’t work out.',
    render: (items) => <CodeList items={items} />
  }
]

const RunList = ({ onSelect }) => {
  const { data: runs, isLoading, error } = useAdminConfigKeySyncRuns()

  if (isLoading) return <CenteredSpinner />
  if (error) return <Alert variant="danger">Error loading sync history.</Alert>
  if (runs.length === 0) return <Alert variant="info">No config syncs have run yet.</Alert>

  return (
    <Table size="sm" hover className="mb-0" data-testid="config-sync-runs">
      <thead>
        <tr>
          <th>Synced</th>
          <th className="text-end">Keys created</th>
          <th className="text-end">Keys attached</th>
          <th className="text-end">Defaults set</th>
          <th className="text-end">Needs attention</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {runs.map((run) => {
          const drift = driftCount(run.counts)
          return (
            <tr key={run.id} data-testid="config-sync-run-row">
              <td>{formatTime(run.synced_at)}</td>
              <td className="text-end">{run.counts.created_keys}</td>
              <td className="text-end">{run.counts.attached}</td>
              <td className="text-end">{run.counts.defaults_backfilled}</td>
              <td className="text-end">
                {drift > 0 ? <AppBadge variant="warning">{drift}</AppBadge> : <span className="text-muted">0</span>}
              </td>
              <td className="text-end">
                <Button size="sm" variant="light" className="text-primary" onClick={() => onSelect(run.id)}>
                  View report
                </Button>
              </td>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}

const RunReport = ({ runId }) => {
  const { data: run, isLoading, error } = useAdminConfigKeySyncRun({ runId })

  if (isLoading) return <CenteredSpinner />
  if (error) return <Alert variant="danger">Error loading this sync’s report.</Alert>

  return (
    <div data-testid="config-sync-report">
      <p className="text-muted small">Synced {formatTime(run.synced_at)}</p>
      <Accordion alwaysOpen>
        {SECTIONS.map((section) => {
          const count = run.counts[section.key] || 0
          const value = run.report[section.key] ?? (section.key === 'defaults_backfilled' ? 0 : [])
          return (
            <Accordion.Item key={section.key} eventKey={section.key} data-testid={`report-section-${section.key}`}>
              <Accordion.Header>
                <span className="me-2">{section.title}</span>
                <AppBadge variant={count > 0 && DRIFT_SECTIONS.includes(section.key) ? 'warning' : 'info'}>
                  {count}
                </AppBadge>
              </Accordion.Header>
              <Accordion.Body>
                <p className="small text-muted">{section.help}</p>
                {count > 0 ? section.render(value, run.analytic_ids || {}) : <span className="text-muted">None.</span>}
              </Accordion.Body>
            </Accordion.Item>
          )
        })}
      </Accordion>
    </div>
  )
}

const ConfigSyncHistoryModal = ({ show, onHide, initialRunId = null }) => {
  const [runId, setRunId] = useState(initialRunId)

  useEffect(() => {
    if (show) setRunId(initialRunId)
  }, [show, initialRunId])

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{runId ? 'Config sync report' : 'Config sync history'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{show && (runId ? <RunReport runId={runId} /> : <RunList onSelect={setRunId} />)}</Modal.Body>
      <Modal.Footer>
        {runId && (
          <Button variant="light" className="text-primary me-auto" onClick={() => setRunId(null)}>
            Back to history
          </Button>
        )}
        <Button variant="light" className="text-primary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ConfigSyncHistoryModal
